import { useMsal } from '@azure/msal-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotify } from '../../components/NotificationProvider';

// local imports
import azureClient from '../../api/azureClient';
import FilterBar from '../../components/FilterBar';
import FilterBarErrorBoundary from '../../components/FilterBarErrorBoundary';
import useUserAuth from '../../hooks/useUserAuth';
import {
  bodPositions,
  calculateLeadHealth,
  calculateSizeBucket,
  newStatuses,

  getStatusStage,
  statusMatchesStages,
  enhancedStatusColors,
  getOrderedStages,
  getStatusesForStages,
} from '../../constants/constants';
import { useDataRefresh } from '../../contexts/DataRefreshContext';
import { getAssociationProperty } from '../../utils/dataModelHelpers';

// Pipeline stage colors for consistency
const PIPELINE_COLORS = {
  'Lead Stage': '#1976d2',
  'Discovery Stage': '#0288d1', 
  'Proposal Stage': '#ff9800',
  'Contract Stage': '#d32f2f',
  'Closed Stage': '#757575'
};
import AssociationForm from './Form';
import StatusFilter from './StatusFilter';

import FileDownload from '@mui/icons-material/Download';
import HelpOutline from '@mui/icons-material/HelpOutline';
import Notes from '@mui/icons-material/Notes';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import './style.css';
// Removed local horizontal tabs (Associations / Next Actions) in favor of left nav
// import ActionItems from "../ActionItems/ActionItems";

import TimelineRenderer from '../../components/TimelineRenderer';

import { AssociationsContext } from './context';

console.log('🚀 Associations.jsx module loaded');

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

const CustomLoadingRenderer = () => {
  return <CircularProgress />;
};

// Removed CustomTabPanel / a11yProps helpers (tabs eliminated)

function Associations() {
  console.log('🏗️ Associations component rendering');
  
  // hooks
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshAssociations, refreshTrigger } = useDataRefresh();
  const { showMessage } = useNotify();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAdmin } = useUserAuth();
  const gridRef = useRef();

  // state
  const [associations, setAssociations] = useState(null);
  console.log(`📋 [State] associations: ${associations ? associations.length + ' items' : 'null'}`);

  // Sort option from URL (default: 'created')
  const sortBy = searchParams.get('sort') || 'created';

  // Debug: Track if component is mounted
  console.log('✨ [Associations] Component mounted, now rendering...');
    const [legendOpen, setLegendOpen] = useState(false);

    const colState = localStorage.getItem('colState');
    // Removed activeTab state

    // Standardized filter bar state
    const [filterBarState, setFilterBarState] = useState(() => {
      // Calculate default statuses inside the initializer
      const defaultStatuses = Object.keys(newStatuses).filter(s => s !== 'Closed Lost');

      // Attempt restore from localStorage
      try {
        const raw = localStorage.getItem('associationsFilterBarState');

        if (raw) {
          const parsed = JSON.parse(raw);

          const state = {
            statuses: new Set(parsed.statuses || defaultStatuses),
            leadTypes: new Set(parsed.leadTypes || []),
            strategies: new Set(parsed.strategies || []),
            selectedStages: new Set(parsed.selectedStages || []),
            funnelStages: new Set(parsed.funnelStages || []),
            text: parsed.text || '',
            lastUpdatedDays: parsed.lastUpdatedDays === undefined ? null : parsed.lastUpdatedDays,
            lastEngagedDays: parsed.lastEngagedDays === undefined ? null : parsed.lastEngagedDays,
            lastCreatedDays: parsed.lastCreatedDays === undefined ? null : parsed.lastCreatedDays,
            sizeBuckets: new Set(parsed.sizeBuckets || []),
            duplicatesOnly: !!parsed.duplicatesOnly,
          };

          return state;
        }
      } catch (e) {
        // Silent fallback to default state
      }

      const defaultState = {
        statuses: new Set(defaultStatuses),
        leadTypes: new Set(['New Customer']), // Default to only 'New Customer' type
        strategies: new Set([]),
        selectedStages: new Set([]),
        funnelStages: new Set([]),
        text: '',
        lastUpdatedDays: null,
        lastEngagedDays: null,
        lastCreatedDays: null,
        sizeBuckets: new Set([]),
        duplicatesOnly: false,
      };

      return defaultState;
    });

    const fetchAssociations = useCallback(async () => {
      try {
        console.log('📥 [Associations] Fetching associations...');
        console.log(`⏱️ [Associations] Timeout set to 30 seconds`);
        
        // Use Promise.race to add timeout protection
        // NEW: Use getFilteredAssociations which only fetches recordType='association' records
        const fetchPromise = azureClient.getFilteredAssociations ? 
          azureClient.getFilteredAssociations() : 
          azureClient.getPaginatedAssociations();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('API call timeout (30s)')), 30000)
        );
        
        const data = await Promise.race([fetchPromise, timeoutPromise]);
        
        console.log(`📦 [Associations] Received ${data?.length || 0} associations from API`);
        
        // Validate data
        if (!Array.isArray(data)) {
          throw new Error(`Expected array from API, got ${typeof data}`);
        }
        
        if (data.length === 0) {
          console.warn('⚠️ [Associations] API returned empty array');
        }
        
        // Debug location data in development
        if (import.meta.env.DEV && data && data.length > 0) {
          console.log('🔍 Location Data Analysis:');
          
          // Sample first 3 associations to understand data structure
          const sample = data.slice(0, 3);
          
          sample.forEach((assoc, index) => {
            console.log(`\n--- Association ${index + 1}: ${assoc.name || 'Unknown'} ---`);
            
            // Check all possible location-related fields
            const locationFields = [
              'city', 'state', 'City', 'State', 'CITY', 'STATE',
              'location', 'Location', 'LOCATION',
              'address', 'Address', 'ADDRESS',
              'propertyCity', 'propertyState', 'property_city', 'property_state',
              'associationCity', 'associationState', 'association_city', 'association_state',
              'mailingAddress', 'mailing_address', 'physicalAddress', 'physical_address',
              'fullAddress', 'full_address'
            ];
            
            locationFields.forEach(field => {
              const value = assoc[field];
              if (value !== undefined && value !== null && value !== '') {
                console.log(`  ${field}: "${value}" (${typeof value})`);
              }
            });
            
            // Check nested address objects
            if (assoc.address && typeof assoc.address === 'object') {
              console.log(`  address object:`, assoc.address);
            }
            if (assoc.Address && typeof assoc.Address === 'object') {
              console.log(`  Address object:`, assoc.Address);
            }
          });
          
          // Summary statistics
          const withLocation = data.filter(a => {
            // Quick check for any location data
            return a.city || a.state || a.City || a.State || a.location || a.Location || a.address || a.Address;
          });
          
          console.log(`\n📊 Summary: ${withLocation.length}/${data.length} associations have potential location data`);
        }
        
        console.log(`✅ [Associations] Setting ${data?.length || 0} associations to state`);
        setAssociations(data);
      } catch (err) {
        console.error('❌ [Associations] Error fetching associations:', {
          message: err?.message,
          name: err?.name,
          status: err?.response?.status,
          statusText: err?.response?.statusText,
          data: err?.response?.data,
          fullError: err
        });
        const errorMsg = err?.message || JSON.stringify(err);
        console.error(`🚨 [Associations] Showing error message to user: "${errorMsg}"`);
        showMessage(`Error fetching associations: ${errorMsg}`, {
          severity: 'error',
          persist: true,
        });
        // Still set empty array so UI doesn't break
        setAssociations([]);
      }
    }, [showMessage]);

    // Memoize mobile row click handler
    const handleMobileRowClick = useCallback(
      event => {
        navigate(`/associations/${event.data.id}`);
      },
      [navigate]
    );

    // Initial load and refresh hook: re-run when refreshTrigger increments
    useEffect(() => {
      console.log(`📌 [useEffect] Running fetch trigger (refreshTrigger: ${refreshTrigger})`);
      fetchAssociations();
    }, [fetchAssociations, refreshTrigger]);

    // Monitor associations state changes
    useEffect(() => {
      console.log(`📊 [State Change] associations updated:`, {
        isNull: associations === null,
        count: Array.isArray(associations) ? associations.length : 'not an array',
        firstItem: associations?.[0]?.name || 'none',
        timestamp: new Date().toISOString()
      });
      
      // Diagnostic: Check for duplicate IDs in the loaded data
      if (Array.isArray(associations) && associations.length > 0) {
        const idCount = {};
        const ridCount = {};
        
        associations.forEach(assoc => {
          if (assoc.id) {
            idCount[assoc.id] = (idCount[assoc.id] || 0) + 1;
          }
          if (assoc._rid) {
            ridCount[assoc._rid] = (ridCount[assoc._rid] || 0) + 1;
          }
        });
        
        const duplicateIds = Object.entries(idCount).filter(([_, count]) => count > 1);
        const duplicateRids = Object.entries(ridCount).filter(([_, count]) => count > 1);
        
        if (duplicateIds.length > 0) {
          console.warn(`⚠️ [Data Quality] Found ${duplicateIds.length} duplicate ID values:`, duplicateIds.slice(0, 5));
        }
        if (duplicateRids.length > 0) {
          console.warn(`🚨 [Critical] Found ${duplicateRids.length} duplicate _rid values (should never happen):`, duplicateRids.slice(0, 5));
        }
        
        const missingIds = associations.filter(a => !a.id).length;
        const missingRids = associations.filter(a => !a._rid).length;
        if (missingIds > 0) console.warn(`⚠️ [Data Quality] ${missingIds} associations missing 'id' field`);
        if (missingRids > 0) console.warn(`⚠️ [Data Quality] ${missingRids} associations missing '_rid' field`);
      }
      
      // Store in window for browser console inspection
      window.__ASSOCIATIONS_DEBUG__ = {
        associations: associations ? { count: associations.length, first: associations[0]?.name } : null,
        timestamp: new Date().toISOString()
      };
    }, [associations]);

    const restoreColumns = () => {
      if (colState) {
        const parsedColState = JSON.parse(colState);
        gridRef.current?.api?.applyColumnState({
          state: parsedColState,
          applyOrder: true,
        });
      }
    };

    const StatusChip = params => {
      const status = params.data.status;
      
      // Map status to pipeline stage for color consistency
      const getStatusPipelineStage = (status) => {
        const stageMapping = {
          'New': 'Lead Stage',
          'Attempt to Contact': 'Lead Stage',
          'Engaged': 'Discovery Stage',
          'Discovery': 'Discovery Stage',
          'Proposal': 'Proposal Stage',
          'Refinement': 'Proposal Stage',
          'Retirement': 'Proposal Stage',
          'Contract Sent': 'Contract Stage',
          'Closed Won': 'Closed Stage',
          'Closed Lost': 'Closed Stage',
          'Closed Dead': 'Closed Stage'
        };
        return stageMapping[status] || 'Lead Stage';
      };
      
      const pipelineStage = getStatusPipelineStage(status);
      let backgroundColor = PIPELINE_COLORS[pipelineStage];
      
      // Create subtle variations within each stage
      const stage = getStatusStage(status);
      if (stage) {
        const statusIndex = stage.statuses.indexOf(status);
        const totalInStage = stage.statuses.length;
        
        if (totalInStage > 1) {
          // Create subtle gradient within stage
          const lightness = 0.9 - (statusIndex / totalInStage) * 0.3;
          backgroundColor = `color-mix(in srgb, ${backgroundColor} ${Math.round((1 - lightness) * 100)}%, white)`;
        }
      }

      // Determine best text color for readability
      const getTextColor = (bgColor) => {
        // For specific colors that need lighter dark text
        if (bgColor && (
          bgColor.includes('#f97316') || // Orange
          bgColor.includes('#0ea5e9') || // Light blue  
          bgColor.includes('color-mix') // Generated gradient colors
        )) {
          return '#4a4a4a'; // Lighter gray, less heavy than black
        }
        return 'white'; // White for dark backgrounds
      };

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            backgroundColor: backgroundColor,
            color: getTextColor(backgroundColor),
            fontWeight: 700,
            fontSize: '0.9rem',
            textAlign: 'center',
            px: 0.5
          }}
        >
          {status}
        </Box>
      );
    };

    // Apply filtering logic to associations before using in other functions
    const filteredAssociations = useMemo(() => {
      console.log(`🔄 [FilteredAssociations] Computing from ${associations?.length || 0} associations`);
      
      if (!associations || !Array.isArray(associations)) {
        console.log('⚠️ [FilteredAssociations] Associations not loaded or invalid');
        return { items: [], duplicateNames: new Set() };
      }
      
      let result = associations;
      console.log(`  → Starting with ${result.length} items`);

      // Stage filtering - highest priority
      if (filterBarState.selectedStages?.size > 0) {
        const before = result.length;
        result = result.filter(a => statusMatchesStages(a.status, filterBarState.selectedStages));
        console.log(`  → After stage filter: ${result.length}/${before}`);
      }

      // Apply status filter
      if (filterBarState.statuses.size > 0) {
        const before = result.length;
        result = result.filter(a => filterBarState.statuses.has(a.status));
        console.log(`  → After status filter: ${result.length}/${before}`);
      }

      // Apply lead type filter
      if (filterBarState.leadTypes.size > 0) {
        const before = result.length;
        result = result.filter(a => filterBarState.leadTypes.has(a.leadType));
        console.log(`  → After lead type filter: ${result.length}/${before}`);
      }

      // Apply strategy filter
      if (filterBarState.strategies.size > 0) {
        const before = result.length;
        result = result.filter(a => filterBarState.strategies.has(a.strategy));
        console.log(`  → After strategy filter: ${result.length}/${before}`);
      }

      // Apply funnel stages filter
      if (filterBarState.funnelStages.size > 0) {
        const before = result.length;
        result = result.filter(a => filterBarState.funnelStages.has(a.funnelStage));
        console.log(`  → After funnel stage filter: ${result.length}/${before}`);
      }

      // Apply size bucket filter
      if (filterBarState.sizeBuckets.size > 0) {
        const before = result.length;
        result = result.filter(a => {
          const sizeBucket = calculateSizeBucket(getAssociationProperty.unitCount(a));
          return filterBarState.sizeBuckets.has(sizeBucket);
        });
        console.log(`  → After size bucket filter: ${result.length}/${before}`);
      }

      // Apply date filters
      const now = new Date();
      
      if (filterBarState.lastUpdatedDays !== null) {
        const before = result.length;
        const cutoffDate = new Date(now.getTime() - (filterBarState.lastUpdatedDays * 24 * 60 * 60 * 1000));
        result = result.filter(a => {
          const updatedDate = a._ts ? new Date(a._ts * 1000) : null;
          return updatedDate && updatedDate >= cutoffDate;
        });
        console.log(`  → After lastUpdated filter: ${result.length}/${before}`);
      }
      
      if (filterBarState.lastEngagedDays !== null) {
        const before = result.length;
        const cutoffDate = new Date(now.getTime() - (filterBarState.lastEngagedDays * 24 * 60 * 60 * 1000));
        result = result.filter(a => {
          const engagedDate = a.lastActivity ? new Date(a.lastActivity) : null;
          return engagedDate && engagedDate >= cutoffDate;
        });
        console.log(`  → After lastEngaged filter: ${result.length}/${before}`);
      }
      
      if (filterBarState.lastCreatedDays !== null) {
        const before = result.length;
        const cutoffDate = new Date(now.getTime() - (filterBarState.lastCreatedDays * 24 * 60 * 60 * 1000));
        result = result.filter(a => {
          const createdDate = a.initialLeadDate ? new Date(a.initialLeadDate) : null;
          return createdDate && createdDate >= cutoffDate;
        });
        console.log(`  → After lastCreated filter: ${result.length}/${before}`);
      }

      // Apply text search
      if (filterBarState.text) {
        const before = result.length;
        const searchText = filterBarState.text.toLowerCase();
        result = result.filter(
          a =>
            (a.name || '').toLowerCase().includes(searchText) ||
            (a.contact || '').toLowerCase().includes(searchText) ||
            (a.address || '').toLowerCase().includes(searchText)
        );
        console.log(`  → After text search: ${result.length}/${before}`);
      }

      // Apply duplicates filter
      const duplicateNames = new Set();
      if (filterBarState.duplicatesOnly) {
        const before = result.length;
        const nameCount = {};
        result.forEach(a => {
          const nameLower = (a.name || '').toLowerCase().trim();
          nameCount[nameLower] = (nameCount[nameLower] || 0) + 1;
        });
        
        result.forEach(a => {
          const nameLower = (a.name || '').toLowerCase().trim();
          if (nameCount[nameLower] > 1) {
            duplicateNames.add(nameLower);
          }
        });

        result = result.filter(a => {
          const nameLower = (a.name || '').toLowerCase().trim();
          return nameCount[nameLower] > 1;
        });
        console.log(`  → After duplicates filter: ${result.length}/${before}`);
      }

      // Apply sorting
      if (sortBy === 'created') {
        // Sort by creation timestamp (newest first)
        result.sort((a, b) => (b._ts || 0) - (a._ts || 0));
        console.log(`  → Sorted by creation date (newest first)`);
      } else if (sortBy === 'updated') {
        // Sort by last update timestamp (most recent first)
        result.sort((a, b) => {
          const aTime = a.lastModified ? new Date(a.lastModified).getTime() : (a._ts || 0) * 1000;
          const bTime = b.lastModified ? new Date(b.lastModified).getTime() : (b._ts || 0) * 1000;
          return bTime - aTime;
        });
        console.log(`  → Sorted by last updated (most recent first)`);
      }

      console.log(`✅ [FilteredAssociations] Final result: ${result.length} items`);
      return { items: result, duplicateNames };
    }, [associations, filterBarState, sortBy]);

    const getRowClass = useCallback(
      params => {
        if (params.data.status === 'Closed Lost' || params.data.status === 'Closed Won') {
          return 'done';
        }
        if (params.data.status === 'New') {
          return 'new';
        }
        if (filterBarState.duplicatesOnly) {
          const nameLower = (params.data.name || '').toLowerCase().trim();
          if (filteredAssociations?.duplicateNames?.has(nameLower)) return 'duplicate-row';
        }
      },
      [filterBarState.duplicatesOnly, filteredAssociations?.duplicateNames]
    );

    // Date comparator for proper chronological sorting
    const dateComparator = useCallback((date1, date2) => {
      // Handle null/undefined values
      if (!date1 && !date2) return 0;
      if (!date1) return -1;
      if (!date2) return 1;

      // Convert to Date objects for comparison
      const d1 = new Date(date1);
      const d2 = new Date(date2);

      // Handle invalid dates
      if (isNaN(d1.getTime()) && isNaN(d2.getTime())) return 0;
      if (isNaN(d1.getTime())) return -1;
      if (isNaN(d2.getTime())) return 1;

      // Compare timestamps
      return d1.getTime() - d2.getTime();
    }, []);

    // Filter handler for StatusFilter component - must be defined before columnDefs
    const handleFilter = useCallback(statuses => {
      // legacy hook (StatusFilter component) -> sync into filter bar
      setFilterBarState(s => ({ ...s, statuses: new Set(statuses) }));
      localStorage.setItem('associationsSelectedStatuses', JSON.stringify(statuses));
    }, []);

    // Pre-compute health scores once when filtered associations change
    // This avoids expensive recalculation on every cell render and sort
    const healthScoresMap = useMemo(() => {
      if (!filteredAssociations?.items) return new Map();
      const map = new Map();
      filteredAssociations.items.forEach(a => {
        if (a?.id) {
          map.set(a.id, calculateLeadHealth(a));
        }
      });
      console.log(`📊 [Health] Pre-computed health scores for ${map.size} associations`);
      return map;
    }, [filteredAssociations?.items]);

    const columnDefs = useMemo(() => {
      return [
        {
          field: 'leadHealth',
          headerName: 'Health',
          headerTooltip: 'Lead health score and priority indicator based on engagement and value',
          width: isMobile ? 80 : 120,
          pinned: 'left',
          cellRenderer: params => {
            if (!params.data) return null;

            // Use pre-computed health from map (O(1) lookup vs O(n) calculation)
            const healthData = healthScoresMap.get(params.data.id) || { score: 0, healthColor: '#666', healthLevel: 'Unknown' };
            const unitCount = getAssociationProperty.unitCount(params.data);

            // Simplified health design with clean visual indicators
            const healthPercentage = Math.max(0, Math.min(100, healthData.score));
            const barColor = healthData.healthColor || '#666';

            // Simple health indicators
            let healthIcon = '🌱';
            let healthLevel = 'Weak';
            if (healthPercentage >= 60) {
              healthIcon = '🔥';
              healthLevel = 'Excellent';
            } else if (healthPercentage >= 30) {
              healthIcon = '⚡';
              healthLevel = 'Strong';
            } else if (healthPercentage >= 10) {
              healthIcon = '📊';
              healthLevel = 'Moderate';
            } else if (healthPercentage < 0) {
              healthIcon = '⚠️';
              healthLevel = 'Poor';
            }

            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  height: '100%',
                  padding: '4px'
                }}
              >
                {/* Simple health icon and score */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span
                    style={{
                      fontSize: '16px',
                      color: barColor
                    }}
                    title={`Health Score: ${healthData.score}/100 - ${healthData.healthLevel}\nUnits: ${unitCount}`}
                  >
                    {healthIcon}
                  </span>
                  <span
                    style={{
                      fontSize: '0.8rem',
                      color: barColor,
                      fontWeight: 'bold',
                      marginLeft: '4px'
                    }}
                  >
                    {healthData.score}
                  </span>
                </Box>
              </Box>
            );
          },
          comparator: (a, b, nodeA, nodeB) => {
            // Use pre-computed health from map (O(1) lookup vs O(n) calculation)
            const healthA = healthScoresMap.get(nodeA.data?.id) || { score: 0 };
            const healthB = healthScoresMap.get(nodeB.data?.id) || { score: 0 };
            return healthA.score - healthB.score;
          },
        },
        {
          field: 'name',
          headerName: 'Name',
          headerTooltip:
            'Click to open association details. Priority indicator (🔥 High, ⚡ Medium, 🌱 Low) - color indicates health score',
          pinned: 'left',
          width: 325, // Increased by 30% from 250
          minWidth: 200,
          editable: false,
          cellClass: 'clickable-name-cell',
          onCellClicked: params => {
            console.log('📛 Name Cell Clicked:', params.data?.id);
            if (params.data?.id) {
              navigate(`/associations/${params.data.id}`);
            }
          },
          cellRenderer: params => {
            if (!params.data) return null;

            return (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  padding: '8px',
                }}
                title="Click to open association details"
              >
                <span 
                  style={{ 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    color: '#1976d2',
                    fontWeight: 500,
                  }}
                >
                  {params.data.name || params.data.associationName || 'Unnamed'}
                </span>
              </Box>
            );
          },
        },
        {
          field: 'location',
          headerName: 'Location',
          headerTooltip: 'Physical location of the association property',
          width: 160,
          valueGetter: (params) => {
            const row = params.data;
            if (!row) return '—';
            
            // Enhanced address parsing for missing commas
            const extractCityState = (addressStr) => {
              if (!addressStr || typeof addressStr !== 'string') return null;
              
              // Remove extra whitespace and normalize
              const normalized = addressStr.trim().replace(/\s+/g, ' ');
              
              // Pattern 1: Standard "City, ST" or "City, ST ZIP" (ZIP may have comma or space before it)
              let match = normalized.match(/,\s*([^,]+),?\s*([A-Z]{2})(?:[,\s]+\d{5}(?:-\d{4})?)?$/i);
              if (match) return `${match[1].trim()}, ${match[2].toUpperCase()}`;
              
              // Pattern 2: "City, State Name" (full state)
              match = normalized.match(/([A-Za-z\s]+),\s*([A-Za-z\s]{4,})$/);
              if (match) return `${match[1].trim()}, ${match[2].trim()}`;
              
              // Pattern 3: Missing comma - "Street City ST" or "Street City ST ZIP"
              // More aggressive matching - look for 2-letter state at end
              match = normalized.match(/\b([A-Za-z\s]{2,})\s+([A-Z]{2})(?:\s+\d{5}(?:-\d{4})?)?$/i);
              if (match) {
                const cityPart = match[1].trim();
                const state = match[2].toUpperCase();
                const words = cityPart.split(/\s+/);

                // Street suffixes to exclude from city names
                const streetSuffixes = ['ST', 'AVE', 'BLVD', 'DR', 'CT', 'RD', 'LN', 'WAY', 'PL', 'CIR', 'TER', 'PKWY', 'HWY'];

                // Try progressively: 3 words, 2 words, 1 word
                for (const wordCount of [3, 2, 1]) {
                  if (words.length >= wordCount) {
                    const candidateCity = words.slice(-wordCount).join(' ');
                    const candidateWords = candidateCity.split(/\s+/);

                    // First word shouldn't be a street suffix (e.g., "Ave Miami" is wrong)
                    const firstWordIsSuffix = streetSuffixes.includes(candidateWords[0]?.toUpperCase());
                    // Last word shouldn't be a street suffix either
                    const lastWordIsSuffix = streetSuffixes.includes(candidateWords[candidateWords.length - 1]?.toUpperCase());
                    // Shouldn't contain numbers
                    const hasNumbers = candidateCity.match(/\d/);

                    if (!firstWordIsSuffix && !lastWordIsSuffix && !hasNumbers && candidateCity.length > 1) {
                      return `${candidateCity}, ${state}`;
                    }
                  }
                }
              }
              
              // Pattern 4: Simple "City ST" format
              match = normalized.match(/^([A-Za-z\s]+)\s+([A-Z]{2})$/i);
              if (match) {
                return `${match[1].trim()}, ${match[2].toUpperCase()}`;
              }

              // Pattern 5: Address ending with ZIP only - extract city before ZIP
              // e.g., "123 Main St, Seattle 98101" or "Seattle 98101"
              match = normalized.match(/,?\s*([A-Za-z][A-Za-z\s]{1,25})\s+(\d{5})(?:-\d{4})?$/);
              if (match) {
                const cityCandidate = match[1].trim();
                // Validate it's not a street suffix
                const streetSuffixes = ['St', 'Ave', 'Blvd', 'Dr', 'Ct', 'Rd', 'Ln', 'Way', 'Pl', 'Cir', 'Ter', 'Pkwy', 'Hwy', 'Street', 'Avenue'];
                if (!streetSuffixes.some(s => cityCandidate.toLowerCase().endsWith(s.toLowerCase()))) {
                  return cityCandidate;
                }
              }

              // Pattern 6: Look for state abbreviation anywhere followed by ZIP
              // e.g., "PO Box 123, WA 98101"
              match = normalized.match(/\b([A-Z]{2})\s+\d{5}/i);
              if (match) {
                const state = match[1].toUpperCase();
                // Valid US state abbreviations
                const validStates = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];
                if (validStates.includes(state)) {
                  return state; // Return just state if we can't find city
                }
              }

              return null;
            };
            
            // Helper to validate city name (should not contain street numbers/addresses)
            const isValidCityName = (city) => {
              if (!city || typeof city !== 'string') return false;
              const trimmed = city.trim();
              // City name should not start with a number (street address)
              if (/^\d/.test(trimmed)) return false;
              // City name should not contain common street suffixes
              if (/\b(St|Ave|Blvd|Dr|Ct|Rd|Ln|Way|Pl|Cir|Ter|Pkwy|Hwy|Street|Avenue|Boulevard|Drive|Court|Road|Lane)\b/i.test(trimmed)) return false;
              // City name should be reasonable length (not full address)
              if (trimmed.length > 30) return false;
              return true;
            };

            // Try direct city/state fields first (with validation)
            if (row.city && row.state && isValidCityName(row.city)) {
              return `${row.city.trim()}, ${row.state.toString().trim().toUpperCase()}`;
            }
            if (row.City && row.State && isValidCityName(row.City)) {
              return `${row.City.trim()}, ${row.State.toString().trim().toUpperCase()}`;
            }
            // Check _parsed fields (from form submission)
            if (row._parsedCity && row._parsedState && isValidCityName(row._parsedCity)) {
              return `${row._parsedCity.trim()}, ${row._parsedState.toString().trim().toUpperCase()}`;
            }
            
            // Try parsing address string with enhanced logic
            const addressFields = [row.address, row.Address, row.location, row.Location];
            for (const addr of addressFields) {
              const cityState = extractCityState(addr);
              if (cityState) return cityState;
            }
            
            // Debug logging for problematic addresses (20% sampling for better visibility)
            if (import.meta.env.DEV && Math.random() < 0.2) {
              const locationFields = Object.keys(row).filter(f => 
                f.toLowerCase().includes('city') || 
                f.toLowerCase().includes('state') || 
                f.toLowerCase().includes('location') || 
                f.toLowerCase().includes('address')
              );
              console.log('🔍 Location parsing debug for:', row.name, {
                address: row.address,
                Address: row.Address, 
                location: row.location,
                city: row.city,
                state: row.state,
                allLocationFields: Object.fromEntries(locationFields.map(f => [f, row[f]])),
                rawAddressAttempt: row.address ? extractCityState(row.address) : 'no address'
              });
            }
            
            // Fallback: check ANY field containing location keywords
            const allFields = Object.keys(row);
            const locationFields = allFields.filter(f => 
              f.toLowerCase().includes('city') || 
              f.toLowerCase().includes('state') || 
              f.toLowerCase().includes('location')
            );
            
            for (const field of locationFields) {
              const value = row[field];
              if (value && typeof value === 'string' && value.length > 0 && value.length < 100) {
                const parsed = extractCityState(value);
                if (parsed) return parsed;
                // Return raw value if it looks reasonable
                if (!value.match(/\d{3,}/) && value.split(' ').length <= 4) {
                  return value.trim();
                }
              }
            }
            
            return '—';
          },
          cellRenderer: (params) => {
            const location = params.value;
            if (!location || location === '—') {
              return (
                <Box
                  sx={{
                    color: '#999',
                    fontStyle: 'italic',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%'
                  }}
                  title="Location data not available or not recognized"
                >
                  <span style={{ opacity: 0.6 }}>—</span>
                </Box>
              );
            }
            
            const googleMapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(location)}`;
            
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%'
                }}
              >
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#1976d2',
                    textDecoration: 'none',
                    fontSize: '0.875rem'
                  }}
                  onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                  onClick={(e) => e.stopPropagation()}
                  title={location}
                >
                  {location}
                </a>
              </Box>
            );
          },
        },
        {
          field: 'pipeline',
          headerName: 'Pipeline',
          headerTooltip: 'Current pipeline stage in the sales process',
          width: 120,
          sortable: false,
          suppressHeaderMenuButton: true,
          suppressMovable: true,
          suppressFiltersToolPanel: true,
          suppressColumnsToolPanel: true,
          filter: false,
          menuTabs: [],
          valueGetter: (params) => {
            const status = params.data.status;
            if (!status) return 'Unknown';
            
            // Map status to pipeline stage
            const stageMapping = {
              'New': 'Lead Stage',
              'Attempt to Contact': 'Lead Stage',
              'Engaged': 'Discovery Stage',
              'Discovery': 'Discovery Stage',
              'Proposal': 'Proposal Stage',
              'Refinement': 'Proposal Stage',
              'Retirement': 'Proposal Stage',
              'Contract Sent': 'Contract Stage',
              'Closed Won': 'Closed Stage',
              'Closed Lost': 'Closed Stage',
              'Closed Dead': 'Closed Stage'
            };
            
            return stageMapping[status] || 'Lead Stage';
          },
          cellRenderer: (params) => {
            const stage = params.value;
            
            // Stage colors matching pipeline buttons
            const getStageColor = (stageName) => {
              const stageColors = {
                'Lead Stage': '#1976d2',
                'Discovery Stage': '#0288d1',
                'Proposal Stage': '#ff9800',
                'Contract Stage': '#d32f2f',
                'Closed Stage': '#757575'
              };
              return stageColors[stageName] || '#757575';
            };
            
            const color = getStageColor(stage);
            
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%'
                }}
              >
                <Chip
                  label={stage.replace(' Stage', '')}
                  size="small"
                  sx={{
                    backgroundColor: color,
                    color: 'white',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: '24px'
                  }}
                />
              </Box>
            );
          },
        },
        {
          field: 'status',
          headerName: 'Status',
          headerTooltip: 'Current detailed status within the pipeline stage',
          width: 140,
          sortable: false,
          suppressHeaderMenuButton: true,
          suppressMovable: true,
          suppressFiltersToolPanel: true,
          suppressColumnsToolPanel: true,
          filter: false,
          menuTabs: [],
          cellRenderer: (params) => {
            const status = params.data.status;
            if (!status) return '';
            
            // Get color based on pipeline stage
            const stageMapping = {
              'New': 'Lead Stage',
              'Attempt to Contact': 'Lead Stage',
              'Engaged': 'Discovery Stage',
              'Discovery': 'Discovery Stage',
              'Proposal': 'Proposal Stage',
              'Refinement': 'Proposal Stage',
              'Retirement': 'Proposal Stage',
              'Contract Sent': 'Contract Stage',
              'Closed Won': 'Closed Stage',
              'Closed Lost': 'Closed Stage',
              'Closed Dead': 'Closed Stage'
            };
            
            const stage = stageMapping[status] || 'Lead Stage';
            
            const getStageColor = (stageName) => {
              const stageColors = {
                'Lead Stage': '#1976d2',
                'Discovery Stage': '#0288d1',
                'Proposal Stage': '#ff9800',
                'Contract Stage': '#d32f2f',
                'Closed Stage': '#757575'
              };
              return stageColors[stageName] || '#757575';
            };
            
            const color = getStageColor(stage);
            
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%'
                }}
              >
                <Chip
                  label={status}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: color,
                    color: color,
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: '24px',
                    backgroundColor: `${color}08`
                  }}
                />
              </Box>
            );
          },
        },
        {
          field: 'timeline',
          headerName: 'Timeline',
          headerTooltip: 'Visual timeline showing creation, updates, actions, and status. Hover for details. Sorts by creation date.',
          width: isMobile ? 45.5 : 58.5,
          pinned: false,
          sortable: true,
          suppressHeaderMenuButton: true,
          suppressMovable: true,
          suppressFiltersToolPanel: true,
          suppressColumnsToolPanel: true,
          filter: false,
          menuTabs: [],
          comparator: (valueA, valueB, nodeA, nodeB) => {
            // Sort by actual creation timestamp (_ts), not initialLeadDate
            const tsA = nodeA.data?._ts;
            const tsB = nodeB.data?._ts;

            // Handle null/undefined - push to end
            if (!tsA && !tsB) return 0;
            if (!tsA) return 1;
            if (!tsB) return -1;

            // Compare timestamps (_ts is in seconds, not milliseconds)
            return tsA - tsB;
          },
          cellRenderer: params => (
            <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '8px' }}>
              <TimelineRenderer data={params.data} />
            </Box>
          ),
          headerClass: 'ag-header-cell-center',
        },
        {
          field: 'leadType',
          headerName: 'Lead Type',
          headerTooltip:
            'Type of lead: 🆕 New Customer, 🔄 Renewal, 🤝 Retention, 📝 Change Order, 👤 Customer',
          width: 30,
          sortable: false,
          suppressHeaderMenuButton: true,
          suppressMovable: true,
          suppressFiltersToolPanel: true,
          suppressColumnsToolPanel: true,
          filter: false,
          menuTabs: [],
          cellRenderer: params => {
            const leadTypeIcons = {
              'New Customer': { icon: '✨', tooltip: 'New Customer' },
              Renewal: { icon: '🔄', tooltip: 'Renewal' },
              Retention: { icon: '🤝', tooltip: 'Retention' },
              'Change Order': { icon: '📝', tooltip: 'Change Order' },
              Customer: { icon: '👤', tooltip: 'Customer' },
            };
            const config = leadTypeIcons[params.data.leadType];
            if (!config) return params.data.leadType || '';

            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                }}
                title={config.tooltip}
              >
                {config.icon}
              </Box>
            );
          },
        },
        {
          field: 'selfManaged',
          headerName: 'Managed By',
          headerTooltip: 'Indicates how the association is currently managed',
          width: 100,
          sortable: false,
          suppressHeaderMenuButton: true,
          suppressMovable: true,
          suppressFiltersToolPanel: true,
          suppressColumnsToolPanel: true,
          filter: false,
          menuTabs: [],
          cellRenderer: params => {
            const selfManagedValue = params.data.selfManaged;
            
            // If unknown/undefined, show nothing
            if (selfManagedValue === undefined || selfManagedValue === null || selfManagedValue === '') {
              return null;
            }
            
            const isSelfManaged = selfManagedValue === true || selfManagedValue === 'true';
            
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  width: '100%'
                }}
              >
                {isSelfManaged ? (
                  // Yes - show 1 icon
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#2e7d32',
                      fontSize: '16px'
                    }}
                    title="Self-managed association"
                  >
                    <span>👥</span>
                  </Box>
                ) : (
                  // No - show 2 icons
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      color: '#757575',
                      fontSize: '14px'
                    }}
                    title="Professionally managed association"
                  >
                    <span>👥</span>
                    <span>🏢</span>
                  </Box>
                )}
              </Box>
            );
          }
        },
        {
          field: 'contact',
          headerName: 'Contact',
          headerTooltip: 'Primary contact person for this association',
          cellEditor: 'agTextCellEditor',
          width: 256, // Reduced to 50% of previous width
          hide: isMobile, // Hide on mobile
          valueGetter: params => {
            const data = params.data;
            
            // Check for multi-contact system first
            if (data.contacts && data.contacts.length > 0) {
              const primaryContact = data.contacts.find(c => c.isPrimary) || data.contacts[0];
              
              // Use the 'name' field from contacts array
              const contactName = primaryContact.name || '';
              const role = primaryContact.role || '';
              
              if (contactName) {
                const nameParts = contactName.split(' ');
                if (nameParts.length > 1) {
                  const firstName = nameParts[0];
                  const lastInitial = nameParts[nameParts.length - 1].charAt(0) + '.';
                  const displayName = `${firstName} ${lastInitial}`;
                  return role ? `${displayName} (${role})` : displayName;
                }
                return role ? `${contactName} (${role})` : contactName;
              }
            }
            
            // Fallback to legacy contact fields
            const contactName = getAssociationProperty.contactName(data);
            const role = data.contactRole || data.bodPosition || '';
            
            if (contactName) {
              const nameParts = contactName.split(' ');
              if (nameParts.length > 1) {
                const firstName = nameParts[0];
                const lastInitial = nameParts[nameParts.length - 1].charAt(0) + '.';
                const displayName = `${firstName} ${lastInitial}`;
                return role ? `${displayName} (${role})` : displayName;
              }
              return role ? `${contactName} (${role})` : contactName;
            }
            
            return '';
          },
        },
        {
          field: 'sizeBucket',
          headerName: 'Size Bucket',
          headerTooltip:
            'Property size category based on number of units (Small: <25, Medium: 25-90, Large: >90)',
          valueGetter: params => {
            const unitCount = getAssociationProperty.unitCount(params.data);
            if (!unitCount || unitCount === 0) return '';
            const sizeBucket = calculateSizeBucket(unitCount);
            return sizeBucket === 'N/A' ? '' : sizeBucket;
          },
          filter: true,
          width: 90,
          hide: isMobile, // Hide on mobile
        },
        {
          field: 'numberOfUnits',
          headerName: 'Number of Units',
          headerTooltip: 'Total number of units/doors in the association property',
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: bodPositions,
          },
          valueGetter: params => {
            const unitCount = getAssociationProperty.unitCount(params.data);
            return unitCount || '';
          },
          filter: true,
        },
        {
          field: 'monthlyRevenue',
          headerName: 'Deal Size',
          headerTooltip: 'Estimated monthly revenue from this association contract',
          valueFormatter: params => {
            const amount = getAssociationProperty.monthlyRevenue(params.data);
            if (!amount || amount === 0) return '';
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(amount);
          },
          filter: 'agNumberColumnFilter',
          width: 120,
        },
        {
          field: 'desiredRange',
          headerName: 'Desired Range',
          headerTooltip: "Customer's desired price range for services",
          cellEditor: 'agTextCellEditor',
          width: 140,
          hide: true, // Hidden by default, can be shown via column menu
        },
        {
          field: 'budgetRange',
          headerName: 'Budget Range',
          headerTooltip: "Customer's budget range for association management services",
          cellEditor: 'agTextCellEditor',
          width: 140,
          hide: true, // Hidden by default, can be shown via column menu
        },
        {
          field: 'associationType',
          headerName: 'Association Type',
          headerTooltip: 'Type of association management structure',
          width: 140,
          valueGetter: params => {
            // Association type is stored in 'type' field
            return params.data.type || '';
          },
          cellRenderer: params => {
            const associationType = params.data.type || '';
            
            // Show icon for association types
            const associationTypeIcons = {
              COA: { icon: '🏢', tooltip: 'COA' },
              HOA: { icon: '🏨', tooltip: 'HOA' },
              Cooperative: { icon: '🏛️', tooltip: 'Cooperative' },
              'Master-Planned': { icon: '🏗️', tooltip: 'Master-Planned' },
            };
            
            const config = associationTypeIcons[associationType];
            const showIcon = associationType && associationType !== 'Unknown' && config;
            
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '14px'
                }}
              >
                {showIcon && (
                  <span title={config.tooltip}>
                    {config.icon}
                  </span>
                )}
                <span>{associationType}</span>
              </Box>
            );
          },
        },

      ];
    }, [isMobile, dateComparator, handleFilter, navigate, healthScoresMap]);

    const filteredColumnDefs = columnDefs.filter(
      col => col.field !== 'Closing' && col.field !== 'Win' && col.field !== 'Attemt to Contact'
    );

    const saveAssociationToDb = async formValues => {
      // Basic validation: prevent creation of associations with empty or suspicious names
      const rawName = (formValues.name || formValues.associationName || '').toString().trim();
      if (!rawName || /^\d+$/.test(rawName) || rawName.length < 3) {
        showMessage(
          'Blocked save: association name looks invalid. Please provide a proper name before saving.',
          {
            severity: 'error',
            persist: false,
          }
        );
        console.warn('[Associations] save blocked due to invalid name:', rawName, formValues);
        return;
      }

      try {
        // Apply defaults before save
        const fv = { ...formValues };
        fv.leadType = fv.leadType || 'New Customer';
        fv.status = fv.status || 'New';

        // ⚠️ MISSING parseLeadDefaults - commented out for now:
        // fv.type = fv.type || parseLeadDefaults.inferAssociationTypeFromText(fv.leadType);
        // if (!fv.numberOfUnits) {
        //   const units =
        //     parseLeadDefaults.inferNumberOfUnitsFromRangeText(fv.leadType) ||
        //     parseLeadDefaults.inferNumberOfUnitsFromRangeText(fv.desiredRange);
        //   if (units) fv.numberOfUnits = units;
        // }

        const associationWithActivity = {
          ...fv,
          lastActivity: new Date().toISOString(),
        };

        console.log('🔵 [saveAssociationToDb] Sending data:', JSON.stringify(associationWithActivity, null, 2));
        const response = await azureClient.post(
          '/save?databaseId=condoshield&containerId=associations',
          associationWithActivity
        );
        console.log('🔵 [saveAssociationToDb] Response:', response.status, response.data);

        // Trigger refresh in other components when association is saved
        // Optimistically add the new association to the current list so the UI updates immediately
        setAssociations(prev => [response.data, ...prev]);

        // Trigger refresh in other components
        refreshAssociations();
        console.log('Association saved, triggering global data refresh...', response.data);

        // Show a user-facing success notification so Add Association isn't silent
        try {
          showMessage('Association saved successfully', { severity: 'success' });
        } catch {
          // ignore if notification system isn't available in this context
        }

        // Navigate to the association detail page
        navigate(`/associations/${response.data.id}`);

        // Return the saved response so callers can await and inspect it (e.g., bulk uploads)
        return response;
      } catch (err) {
        console.error(err);
        const status = err?.response?.status;
        const serverMessage = err?.response?.data?.message || err?.response?.data?.error;
        const message = serverMessage || err.message || 'Unknown error';
        showMessage(`Error saving association: ${status ? `HTTP ${status} - ` : ''}${message}`, {
          severity: 'error',
          persist: true,
        });
      }
    };

    const exportVisibleAssociationsToCSV = () => {
      if (!filteredAssociations?.items || filteredAssociations.items.length === 0) {
        showMessage('No associations to export.', 'warning');
        return;
      }

      // Define association fields to export (matching detail page fields)
      const headers = [
        'Name',
        'Address',
        'Status',
        'Contact',
        'Phone',
        'Email',
        'Type',
        'Lead Type',
        'Created Date',
        'Last Activity',
      ];
      const rows = filteredAssociations.items.map(assoc => {
        const values = [
          assoc.name || assoc.associationName || '',
          assoc.address || '',
          assoc.status || '',
          assoc.contact || assoc.contactName || '',
          assoc.phone || '',
          assoc.email || assoc.contactEmail || '',
          assoc.type || '',
          assoc.leadType || '',
          assoc.initialLeadDate ? new Date(assoc.initialLeadDate).toLocaleDateString() : '',
          assoc.lastActivity ? new Date(assoc.lastActivity).toLocaleDateString() : '',
        ];

        // Properly escape CSV values
        return values.map(value => {
          const stringValue = String(value || '');
          // Escape quotes and wrap in quotes if value contains comma, quote, or newline
          if (
            stringValue.includes(',') ||
            stringValue.includes('"') ||
            stringValue.includes('\n')
          ) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        });
      });

      const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `association_export_${new Date().toISOString().split('T')[0]}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showMessage(
        `Successfully exported ${filteredAssociations?.items?.length || 0} associations to CSV`,
        'success'
      );
    };

    const handleFilterBarChange = useCallback(v => {
      setFilterBarState(prev => {
        // Deep equality check to prevent rerender loops
        const isSame = 
          prev.text === v.text &&
          prev.statuses.size === v.statuses.size &&
          [...prev.statuses].every(s => v.statuses.has(s)) &&
          prev.leadTypes.size === v.leadTypes.size &&
          [...prev.leadTypes].every(t => v.leadTypes.has(t)) &&
          prev.strategies.size === v.strategies.size &&
          [...prev.strategies].every(s => v.strategies.has(s)) &&
          prev.selectedStages.size === (v.selectedStages?.size || 0) &&
          [...(prev.selectedStages || [])].every(s => v.selectedStages?.has(s)) &&
          prev.lastUpdatedDays === v.lastUpdatedDays &&
          prev.lastEngagedDays === v.lastEngagedDays &&
          prev.lastCreatedDays === v.lastCreatedDays &&
          prev.duplicatesOnly === v.duplicatesOnly;

        if (isSame) return prev;
        return v;
      });

      // persist
      const serialisable = {
        statuses: Array.from(v.statuses),
        leadTypes: Array.from(v.leadTypes || []),
        strategies: Array.from(v.strategies),
        selectedStages: Array.from(v.selectedStages || []),
        funnelStages: Array.from(v.funnelStages || []),
        text: v.text,
        lastUpdatedDays: v.lastUpdatedDays,
        lastEngagedDays: v.lastEngagedDays,
        lastCreatedDays: v.lastCreatedDays,
        sizeBuckets: Array.from(v.sizeBuckets || []),
        duplicatesOnly: v.duplicatesOnly,
      };
      localStorage.setItem(
        'associationsFilterBarState',
        JSON.stringify(serialisable)
      );
    }, []);

    const handleFilterBarCommand = useCallback(() => {
      // basic commands: !note <text> adds activity note to ALL filtered associations
      // >StatusName bulk status change on filtered set
    }, []);

    const contextValue = useMemo(() => ({ 
      saveAssociationToDb, 
      associations 
    }), [saveAssociationToDb, associations]);

    try {
      return (
        <AssociationsContext.Provider value={contextValue}>
        <Box data-testid='associations-page'>
          {/* Tabs removed: showing Associations content directly */}
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>


              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant='h5'>Associations</Typography>

                  <IconButton
                    size='small'
                    onClick={() => setLegendOpen(true)}
                    aria-label='Legend / Help'
                  >
                    <HelpOutline fontSize='small' />
                  </IconButton>
                </Box>
                <Button
                  size='small'
                  variant='contained'
                  sx={{ color: 'white' }}
                  onClick={restoreColumns}
                >
                  My View
                </Button>
              </Box>
              <FilterBarErrorBoundary>
                <FilterBar
                  value={filterBarState}
                  onChange={handleFilterBarChange}
                  savedViewsKey='associationsSavedViews'
                  onCommand={handleFilterBarCommand}
                />
              </FilterBarErrorBoundary>

              {/* Sort Selector */}
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                    Sort By:
                  </FormLabel>
                  <RadioGroup
                    row
                    value={sortBy}
                    onChange={(e) => {
                      const newSort = e.target.value;
                      setSearchParams({ sort: newSort });
                    }}
                    sx={{ gap: 2 }}
                  >
                    <FormControlLabel
                      value="created"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">Recently Created</Typography>}
                    />
                    <FormControlLabel
                      value="updated"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">Recently Updated</Typography>}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>

            <Box
              onClick={() => console.log('📦 Grid Wrapper Box Clicked')}
              sx={{
                width: '100%',
                mt: 3,
                display: 'flex',
                flexDirection: 'column',
                // Reserve space for footer (pagination / count) ~48px and header/filter area ~360px
                // Extra padding for Leo widget at bottom
                flex: '1 1 auto',
                minHeight: 0,
                height: 'calc(100vh - 280px)',
                paddingBottom: '80px', // Space for Leo widget
                border: '2px solid transparent', // Placeholder for debugging
              }}
            >
              <Box
                sx={{ flex: '1 1 auto', width: '100%', minHeight: 0 }}
                className={theme.palette.mode === 'dark' ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
              >
                <AgGridReact
                  ref={gridRef}
                  onGridReady={params => {
                    console.log('✅ AG Grid Ready');
                    restoreColumns();
                  }}
                  stopEditingWhenCellsLoseFocus={true}
                  suppressClickEdit={true}
                  defaultColDef={{
                    resizable: true,
                    sortable: true, // Default, but individual columns can override
                    filter: true, // Default, but individual columns can override  
                    tooltipField: undefined,
                    headerTooltip: undefined,
                    minWidth: isMobile ? 80 : 100, // Smaller minimum width on mobile
                    width: isMobile ? 120 : undefined, // Default width on mobile
                  }}
                  suppressMenuHide={true} // Allow individual column menu suppression
                  initialState={{
                    sort: {
                      sortModel: [{ colId: 'initialLeadDate', sort: 'desc' }],
                    },
                  }}
                  tooltipShowDelay={400}
                  columnDefs={filteredColumnDefs}
                  rowData={filteredAssociations?.items || []}
                  getRowId={params => {
                    // Use _rid (Cosmos DB resource ID) as primary key since it's guaranteed unique
                    // Falls back to id, then to name + index combo as last resort
                    if (params.data._rid) {
                      return params.data._rid;
                    }
                    if (params.data.id) {
                      return params.data.id;
                    }
                    // Fallback: use name + data to create unique key
                    return `${params.data.name || 'unnamed'}-${JSON.stringify(params.data).substring(0, 50)}`;
                  }}
                  loadingOverlayComponent={CustomLoadingRenderer}
                  loadingCellRendererParams={{
                    loadingMessage: 'Please wait...',
                  }}
                  loading={associations === null}
                  getRowClass={getRowClass}
                  domLayout='normal'
                  rowSelection={{
                    mode: 'singleRow',
                    enableClickSelection: false
                  }}
                  suppressCellFocus={false}
                  onRowClicked={event => {
                    console.log('🖱️ Row Clicked:', {
                      id: event.data?.id,
                      target: event.event.target.tagName,
                      isTrusted: event.event.isTrusted,
                      ctrlKey: event.event.ctrlKey,
                      metaKey: event.event.metaKey
                    });
                    if (event.data?.id) {
                      // Don't navigate if clicking a link or button
                      const target = event.event.target;
                      if (target.tagName === 'A' || target.closest('a') || target.tagName === 'BUTTON' || target.closest('button')) {
                        console.log('🚫 Navigation suppressed: clicked link or button');
                        return;
                      }

                      // Check if this is a "real" click or a swallowed one
                      if (!event.event.isTrusted) {
                        console.warn('⚠️ Row click event is not trusted (synthetic). Navigation might be blocked.');
                      }

                      const url = `/associations/${event.data.id}`;

                      // Ctrl+Click (or Cmd+Click on Mac) opens in new tab
                      if (event.event.ctrlKey || event.event.metaKey) {
                        console.log(`🆕 Opening in new tab: ${url}`);
                        window.open(url, '_blank');
                      } else {
                        console.log(`🚀 Navigating to association: ${event.data.id}`);
                        navigate(url);
                      }
                    }
                  }}
                  onCellClicked={event => {
                    const colId = event.column.getColId();
                    console.log('🖱️ Cell Clicked:', {
                      colId: colId,
                      field: event.column.getColDef().field,
                      id: event.data?.id,
                      target: event.event.target.tagName
                    });
                    
                    // If it's the name column, force navigation immediately
                    if ((colId === 'name' || event.column.getColDef().field === 'name') && event.data?.id) {
                      const url = `/associations/${event.data.id}`;

                      // Ctrl+Click (or Cmd+Click on Mac) opens in new tab
                      if (event.event.ctrlKey || event.event.metaKey) {
                        console.log('⚡ Name cell Ctrl+clicked - opening in new tab');
                        window.open(url, '_blank');
                      } else {
                        console.log('⚡ Name cell clicked - forcing navigation');
                        navigate(url);
                      }
                    }
                  }}
                  onCellDoubleClicked={event => {
                    console.log('🖱️ Cell Double Clicked (Diagnostic):', event.data?.id);
                  }}
                  suppressRowTransform={false}
                  animateRows={false}
                  rowBuffer={10}
                  debounceVerticalScrollbar={true}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 2,
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssociationForm saveAssociationToDb={saveAssociationToDb} />
                  {isAdmin && (
                    <Tooltip title='Export to CSV'>
                      <IconButton
                        onClick={exportVisibleAssociationsToCSV}
                        sx={{
                          border: '1px solid rgba(0, 0, 0, 0.23)',
                          borderRadius: '4px',
                        }}
                      >
                        <FileDownload />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isMobile && (
                    <Typography variant='caption' color='textSecondary'>
                      Tap rows to view details
                    </Typography>
                  )}
                  <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                    Total associations: {filteredAssociations?.items?.length || 0}
                  </Typography>
                </Box>
              </Box>
              {legendOpen && (
                <Box
                  sx={{
                    position: 'fixed',
                    top: 80,
                    right: 16,
                    width: 340,
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    bgcolor: '#fff',
                    boxShadow: 3,
                    borderRadius: 2,
                    p: 2,
                    zIndex: 1300,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                      Legend & Help
                    </Typography>
                    <Button size='small' onClick={() => setLegendOpen(false)}>
                      Close
                    </Button>
                  </Box>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    Health Score Scale
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <LegendRow
                      color='#2e7d32'
                      label='60+ Excellent'
                      desc='Very recent engagement or closed won momentum.'
                    />
                    <LegendRow
                      color='#0277bd'
                      label='30–59 Strong'
                      desc='Recent activity & advancing stage.'
                    />
                    <LegendRow
                      color='#ffb300'
                      label='10–29 Moderate'
                      desc='Some activity; mid pipeline.'
                    />
                    <LegendRow color='#90caf9' label='0–9 Weak' desc='Aging; limited signals.' />
                    <LegendRow
                      color='#c62828'
                      label='<0 Poor'
                      desc='Stalled or negative events (lost/inactive).'
                    />
                  </Box>
                  <Typography variant='body2' sx={{ fontWeight: 600, mt: 1 }}>
                    Field Definitions
                  </Typography>
                  <Definition
                    term='Health'
                    def='Composite heuristic: recency, status momentum, strategy set bonus, stalled penalty.'
                  />
                  <Definition
                    term='Last Activity'
                    def='Most recent engagement or system update timestamp.'
                  />
                  <Definition term='Engaged ≤ Xd' def='Filter: engagement within X days.' />
                  <Definition term='Updated ≤ Xd' def='Filter: any update within X days.' />
                  <Definition term='Stalled' def='>30 days with no activity.' />
                  <Definition
                    term='Duplicates'
                    def='Same name appears 2+ times in current filtered set.'
                  />
                  <Definition term='Size Bucket' def='Normalized unit count range (doors).'
                  />
                  <Definition term='Strategy' def='Engagement approach (e.g., Pursue, Nurture).'
                  />
                  <Typography variant='body2' sx={{ fontWeight: 600, mt: 1 }}>
                    Timeline Visualization
                  </Typography>
                  <Definition term='w' def='Individual week since creation' />
                  <Definition term='U' def='Week containing last update' />
                  <Definition term='A' def='Open action or follow-up scheduled' />
                  <Definition term='m' def='Multiple consecutive weeks (>4) compressed' />
                  <Definition term='_' def='Today marker (always rightmost time position)' />
                  <Definition term='O' def='Record status is open/active' />
                  <Definition term='X' def='Record status is closed' />
                  <Typography variant='body2' sx={{ fontWeight: 600, mt: 1 }}>
                    Tips
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li>
                      <Typography variant='caption'>
                        Use search for free text; prefix !note or {'>'}Status for bulk actions.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant='caption'>
                        Save custom filter combinations via "Save View".
                      </Typography>
                    </li>
                    <li>
                      <Typography variant='caption'>
                        Health drops after 14d inactivity; act before it weakens.
                      </Typography>
                    </li>
                  </ul>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </AssociationsContext.Provider>
    );
  } catch (error) {
    console.error('🔍 Associations component ERROR:', error);
    console.error('🔍 Error message:', error.message);
    console.error('🔍 Error stack:', error.stack);
    throw error; // Re-throw to trigger RouteErrorBoundary
  }
}

// Enhanced Helper Components
const LegendRow = ({ color, label, desc }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ width: 12, height: 12, bgcolor: color, borderRadius: 0.5 }} />
    <Typography variant='body2' sx={{ fontWeight: 600, minWidth: 80 }}>
      {label}
    </Typography>
    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
      {desc}
    </Typography>
  </Box>
);

const Definition = ({ term, def }) => (
  <Box sx={{ display: 'flex', mb: 0.5 }}>
    <Typography variant='body2' sx={{ fontWeight: 600, minWidth: 80 }}>
      {term}:
    </Typography>
    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
      {def}
    </Typography>
  </Box>
);

export default Associations;
